import { createTheme, type Theme } from "@mui/material/styles";

import { resolveThemeColors, BREAKPOINTS, themeTokens, type ThemeMode } from "@config/theme/tokens";

function buildPalette(mode: ThemeMode) {
	const colors = resolveThemeColors(mode);

	return {
		mode: "light" as const,
		primary: {
			main: colors.primary,
			dark: colors.primaryHover,
			light: colors.secondary,
			contrastText: colors.primaryForeground,
		},
		secondary: {
			main: colors.secondary,
			contrastText: colors.secondaryForeground,
		},
		error: {
			main: colors.danger,
			contrastText: colors.dangerForeground,
		},
		warning: {
			main: colors.warning,
			contrastText: colors.warningForeground,
		},
		success: {
			main: colors.success,
			contrastText: colors.successForeground,
		},
		info: {
			main: colors.info,
			contrastText: colors.infoForeground,
		},
		background: {
			default: colors.background,
			paper: colors.surface,
		},
		text: {
			primary: colors.foreground,
			secondary: colors.muted,
			disabled: colors.mutedForeground,
		},
		divider: colors.borderStrong,
	};
}

export function createMuiTheme(mode: ThemeMode = "light"): Theme {
	const { shared } = themeTokens;

	return createTheme({
		palette: buildPalette(mode),
		typography: {
			fontFamily: shared.typography.fontFamily.sans,
			fontSize: 14,
			h1: {
				fontSize: shared.typography.fontSize["3xl"],
				fontWeight: shared.typography.fontWeight.bold,
			},
			h2: {
				fontSize: shared.typography.fontSize["2xl"],
				fontWeight: shared.typography.fontWeight.semibold,
			},
			h3: {
				fontSize: shared.typography.fontSize.xl,
				fontWeight: shared.typography.fontWeight.semibold,
			},
			body1: { fontSize: shared.typography.fontSize.base },
			body2: { fontSize: shared.typography.fontSize.sm },
			button: {
				fontWeight: shared.typography.fontWeight.semibold,
				textTransform: "none",
			},
		},
		shape: {
			borderRadius: 8,
		},
		breakpoints: {
			values: {
				xs: BREAKPOINTS.xs,
				sm: BREAKPOINTS.sm,
				md: BREAKPOINTS.md,
				lg: BREAKPOINTS.lg,
				xl: BREAKPOINTS.xl,
			},
		},
		components: {
			MuiCssBaseline: {
				styleOverrides: {
					body: {
						backgroundColor: "var(--tvet-background)",
						color: "var(--tvet-foreground)",
					},
				},
			},
			MuiButton: {
				defaultProps: { disableElevation: true },
				styleOverrides: {
					root: {
						textTransform: "none",
						borderRadius: "5px",
						fontWeight: shared.typography.fontWeight.semibold,
					},
					contained: {
						backgroundColor: "var(--tvet-primary)",
						color: "var(--tvet-primary-foreground)",
						"&:hover": {
							backgroundColor: "var(--tvet-primary-hover)",
						},
					},
					outlined: {
						borderColor: "var(--tvet-primary)",
						color: "var(--tvet-primary)",
						"&:hover": {
							backgroundColor: "var(--tvet-secondary)",
							borderColor: "var(--tvet-primary-hover)",
						},
					},
				},
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: {
						borderRadius: "var(--tvet-input-radius)",
						backgroundColor: "var(--tvet-surface)",
						"& .MuiOutlinedInput-notchedOutline": {
							borderColor: "var(--tvet-border)",
						},
						"&:hover .MuiOutlinedInput-notchedOutline": {
							borderColor: "var(--tvet-border-strong)",
						},
						"&.Mui-focused .MuiOutlinedInput-notchedOutline": {
							borderColor: "var(--tvet-primary)",
						},
					},
				},
			},
			MuiPaper: {
				styleOverrides: {
					root: {
						backgroundImage: "none",
						backgroundColor: "var(--tvet-surface)",
					},
				},
			},
			MuiAvatar: {
				styleOverrides: {
					colorDefault: {
						backgroundColor: "var(--tvet-secondary)",
						color: "var(--tvet-secondary-foreground)",
						fontWeight: shared.typography.fontWeight.semibold,
					},
				},
			},
			MuiChip: {
				styleOverrides: {
					root: {
						"&.MuiChip-filledDefault": {
							backgroundColor: "var(--tvet-secondary)",
							color: "var(--tvet-secondary-foreground)",
							border: "1px solid var(--tvet-border-strong)",
							fontWeight: shared.typography.fontWeight.medium,
						},
					},
				},
			},
			MuiSvgIcon: {
				styleOverrides: {
					root: {
						"&.MuiSvgIcon-colorDefault": {
							color: "var(--tvet-primary)",
						},
					},
				},
			},
			MuiCircularProgress: {
				styleOverrides: {
					root: {
						color: "var(--tvet-primary)",
					},
				},
			},
		},
	});
}
